import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api, apiError } from "../lib/api";
import { toast } from "sonner";
import { Loader2, FileText } from "lucide-react";
import { Checkbox } from "./ui/checkbox";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { WAIVER_PDF } from "../lib/constants";

export const WaiverGate = () => {
  const { user, setUser } = useAuth();
  const [checked, setChecked] = useState(false);
  const [legalName, setLegalName] = useState("");
  const [loading, setLoading] = useState(false);

  const needsWaiver = user && user.role === "client" && !user.waiver_accepted;
  const needsLegalName = user && user.role === "client" && !user.legal_name;
  if (!needsWaiver && !needsLegalName) return null;

  const canSubmit = legalName.trim().length >= 2 && (!needsWaiver || checked);

  const accept = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/me/accept-waiver", { legal_name: legalName.trim() });
      setUser(data);
      toast.success(needsWaiver ? "Waiver accepted — welcome to Sculpt Fusion!" : "Thanks — your legal name is on file.");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open>
      <DialogContent data-testid="waiver-gate"
        className="bg-[#0A0A0A] border-neutral-800 text-white max-w-md [&>button]:hidden"
        onPointerDownOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="font-display uppercase text-3xl">{needsWaiver ? "One last step" : "Confirm your legal name"}</DialogTitle>
          <DialogDescription className="text-neutral-400">
            {needsWaiver
              ? "Before you book your first mat, please enter your legal name and accept the studio waiver and policies."
              : "For your signed liability waiver, we need your full legal name as it appears on your ID. Classmates still only see your preferred name or nickname."}
          </DialogDescription>
        </DialogHeader>
        <div>
          <Label htmlFor="waiver-gate-legal-name" className="text-xs text-neutral-400">Full legal name <span className="text-neon">(as on your ID)</span></Label>
          <Input id="waiver-gate-legal-name" autoComplete="name" data-testid="waiver-gate-legal-name" value={legalName}
            onChange={(e) => setLegalName(e.target.value)} placeholder="Jane Marie Doe"
            className="bg-black border-neutral-800 mt-1" />
        </div>
        {needsWaiver && (
          <>
            <div className="flex flex-col gap-2 my-1">
              <a href={WAIVER_PDF} target="_blank" rel="noreferrer" data-testid="waiver-gate-pdf"
                className="inline-flex items-center gap-2 border border-neutral-800 px-4 py-3 text-sm text-neutral-300 hover:border-neon hover:text-neon transition-colors">
                <FileText size={15} className="text-neon" /> Sculpt Fusion Liability Waiver (PDF)
              </a>
              <Link to="/policies" target="_blank" data-testid="waiver-gate-policies"
                className="inline-flex items-center gap-2 border border-neutral-800 px-4 py-3 text-sm text-neutral-300 hover:border-neon hover:text-neon transition-colors">
                <FileText size={15} className="text-neon" /> Sculpt Fusion Policies (incl. no-refund policy)
              </Link>
              <Link to="/terms" target="_blank" data-testid="waiver-gate-terms"
                className="inline-flex items-center gap-2 border border-neutral-800 px-4 py-3 text-sm text-neutral-300 hover:border-neon hover:text-neon transition-colors">
                <FileText size={15} className="text-neon" /> Terms of Service
              </Link>
            </div>
            <label className="flex items-start gap-3 cursor-pointer border border-neon/30 bg-neon/5 p-4">
              <Checkbox checked={checked} onCheckedChange={(v) => setChecked(!!v)} data-testid="waiver-gate-checkbox" className="mt-0.5" />
              <span className="text-sm text-neutral-300 leading-relaxed">
                I, <span className="text-white font-medium">{legalName.trim() || "(your legal name)"}</span>, have read and agree to the Sculpt Fusion Liability Waiver, Studio Policies (including the no-refund policy), and Terms of Service.
              </span>
            </label>
          </>
        )}
        <div>
          <button onClick={accept} disabled={!canSubmit || loading} data-testid="waiver-gate-submit"
            className="w-full inline-flex items-center justify-center gap-2 bg-neon text-black font-semibold text-xs uppercase tracking-[0.2em] py-4 hover:bg-white hover:text-black transition-colors disabled:opacity-40">
            {loading ? <Loader2 size={14} className="animate-spin" /> : null} {needsWaiver ? "Agree & Continue" : "Save & Continue"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
